import { useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import * as Cesium from 'cesium';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import MapPortalService from '../services/MapPortal/mapportal.service';
import GeoserverService from '../services/GeoserverService/geoserver.service';
import MapIconsService from '../services/MapIconService/mapIcon.service';
import { getLabelPosition } from '../shared/utils/labelPositionHelpers';
import { radiusToPixels } from '../shared/utils/heatmapSymbologyHelpers';
import { removeExistingGlowEntities, renderGlowOutline, renderGreyscaleLine, renderGreyscalePolygon } from '../shared/utils/effectsHelpers';
import { ApiRoutes } from '../routes/routeConstants/apiRoutes';
import { isBuiltInShape } from '../views/MapPortal/LayerProperties/Symbology/components/builtInShapes';
import { resolveSingleConfig } from '../views/MapPortal/LayerProperties/Symbology/symbologyConfig.utils';

// Client-side feature work (labels, glow, greyscale, clustering, search)
// instantiates one Cesium object per feature, which explodes the DOM/GPU and
// can OOM the tab on a 10k+ feature layer. Cap how many features we pull for
// that work; the geometry itself ALWAYS renders fully via WMS tiles regardless.
// Override with VITE_APP_MAX_CLIENT_FEATURES.
const MAX_CLIENT_FEATURES = Math.max(1, Number(import.meta.env.VITE_APP_MAX_CLIENT_FEATURES) || 5000);

// WMS endpoint for tiled imagery. Defaults to the direct GeoServer WMS so
// rendering is unchanged; point it at GeoWebCache
// (VITE_APP_GEOSERVER_WMS_PATH=/geoserver/gwc/service/wms) once a matching
// gridset/tile size is configured server-side to serve cached tiles.
const WMS_PATH = import.meta.env.VITE_APP_GEOSERVER_WMS_PATH || '/geoserver/mapaki/wms';

// A per-layer signature of everything that affects the WMS render
// (style/symbology/bounds/visibility). Used to skip the SLD PUT + tile rebuild
// for layers that didn't actually change, so a single edit no longer
// re-requests every layer's tiles. Ordering/UI-only fields are excluded so a
// legend reorder reuses tiles (re-stacking is handled separately). Object keys
// are sorted so an equivalent layer with a different key order (e.g. a preview
// spread) doesn't read as "changed".
const WMS_SIG_EXCLUDE = new Set(['sort_order', 'sortOrder', 'order', 'toggled']);
const wmsSignature = (layer: any): string =>
  JSON.stringify(layer, (key, value) => {
    if (WMS_SIG_EXCLUDE.has(key)) return undefined;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value)
        .sort()
        .reduce((acc: Record<string, unknown>, k) => {
          acc[k] = value[k];
          return acc;
        }, {});
    }
    return value;
  });

export interface UseCesiumLayersArgs {
  isViewerReady: boolean;
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  layers: any[];
  setSearchableFeatures: Dispatch<SetStateAction<any[]>>;
}

export const useCesiumLayers = ({ isViewerReady, viewerRef, layers, setSearchableFeatures }: UseCesiumLayersArgs) => {
  const { fetchFeaturesFromLayer } = MapPortalService();
  const { mutateAsync: fetchFeaturesFromLayerAsync } = useMutation(fetchFeaturesFromLayer());
  const { applyDynamicStyle, applyUniqueStyle, applyQuantityStyle, applyQuantityClassStyle, applyHeatmapStyle } = GeoserverService();
  const { mutateAsync: applyDynamicStyleAsync } = useMutation(applyDynamicStyle());
  const { mutateAsync: applyUniqueStyleAsync } = useMutation(applyUniqueStyle());
  const { mutateAsync: applyQuantityStyleAsync } = useMutation(applyQuantityStyle());
  const { mutateAsync: applyQuantityClassStyleAsync } = useMutation(applyQuantityClassStyle());
  const { mutateAsync: applyHeatmapStyleAsync } = useMutation(applyHeatmapStyle());
  const { fetchIconUrl } = MapIconsService();
  // Share the app's QueryClient so icon-URL fetches benefit from the global
  // cache instead of being walled off in a per-hook client.
  const queryClient = useQueryClient();

  const featureRequestsRef = useRef<Map<string, Promise<any>>>(new Map());
  const featureLayerVersionRef = useRef<Map<string, string>>(new Map());

  // Persistent across layer-effect re-runs: each layer's live WMS imagery layer
  // + its render signature. Lets a re-run reuse unchanged layers' tiles instead
  // of removing and re-requesting every layer's imagery on each edit.
  const imageryRegistryRef = useRef<Map<string, { sig: string; imageryLayer: Cesium.ImageryLayer }>>(new Map());
  // Generation counter so an async build from a superseded run can't commit
  // imagery into a newer run's state.
  const genRef = useRef(0);

  // One AbortController for the lifetime of this map view. Feature reads are
  // cached (in-flight requests are shared across layer-effect re-runs), so we
  // must NOT abort per re-run — only when the map view unmounts, to release
  // any still-pending reads instead of orphaning them.
  const abortRef = useRef<AbortController | null>(null);
  if (!abortRef.current) abortRef.current = new AbortController();
  useEffect(() => () => abortRef.current?.abort(), []);

  const loadUrl = async (selectedIcon: string) => {
    const { queryKey, queryFn } = fetchIconUrl(selectedIcon);
    if (typeof queryFn !== 'function') {
      throw new Error('Query function not available for icon URL.');
    }
    return queryClient.fetchQuery({ queryKey, queryFn });
  };

  // 5. Layer Management
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current) return;
    const viewer = viewerRef.current;
    let spiderfyEntities: Cesium.Entity[] = [];
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    // Bound how many feature reads run at once. A many-layer map otherwise
    // fires one heavy PostGIS read per layer simultaneously; with the browser's
    // ~6-connection-per-host limit and a small DB pool, the tail queued past
    // the request timeout (the "Unable to Fetch Features" errors), worst after
    // idle when connections are cold. Override with VITE_APP_FEATURE_FETCH_CONCURRENCY.
    const FEATURE_FETCH_CONCURRENCY = Math.max(1, Number(import.meta.env.VITE_APP_FEATURE_FETCH_CONCURRENCY) || 4);
    let activeFetches = 0;
    const fetchQueue: Array<() => void> = [];
    const acquireSlot = () =>
      new Promise<void>((resolve) => {
        if (activeFetches < FEATURE_FETCH_CONCURRENCY) {
          activeFetches++;
          resolve();
        } else {
          fetchQueue.push(resolve);
        }
      });
    const releaseSlot = () => {
      activeFetches--;
      const next = fetchQueue.shift();
      if (next) {
        activeFetches++;
        next();
      }
    };

    // Leak fix: this effect re-runs on every `layers` change (style edit,
    // visibility toggle, debounced preview) and registers postRender /
    // camera.changed / cluster listeners + a ScreenSpaceEventHandler. Without
    // teardown they accumulated unboundedly across a session. Track every
    // listener's remover and the handler, and dispose them on cleanup. `track`
    // also removes anything registered AFTER disposal (the loader is async, so
    // a fast re-run can finish registering after cleanup ran).
    let disposed = false;
    const removers: Array<() => void> = [];
    const track = (remove: () => void) => {
      if (disposed) remove();
      else removers.push(remove);
    };
    const warnedLayers = new Set<string>();

    const primitives = viewer.scene.primitives;
    for (let i = primitives.length - 1; i >= 0; i--) {
      const p = primitives.get(i);
      if (p instanceof Cesium.LabelCollection) {
        primitives.remove(p);
      }
    }

    // Preserve interactive drawing data sources across layer rebuilds — a blanket
    // removeAll() here wipes an in-progress sketch (e.g. the Create Feature line/
    // polygon vertices or the Query search-extent), which made multi-click
    // drawing impossible whenever a layer refresh fired mid-draw.
    const PRESERVE_DATA_SOURCES = new Set(['create-feature-draw', 'query-search-extent']);
    for (let i = viewer.dataSources.length - 1; i >= 0; i--) {
      const ds = viewer.dataSources.get(i);
      if (!PRESERVE_DATA_SOURCES.has((ds as Cesium.CustomDataSource).name)) {
        viewer.dataSources.remove(ds, true);
      }
    }
    const imLayers = viewer.scene.imageryLayers;

    // Incremental imagery: rather than removing every WMS layer and rebuilding
    // (which re-requested all tiles on any edit — the post-idle stampede),
    // reconcile against what's already rendered. `keptOrBuilt` = layers that
    // still have imagery this run; `pendingBuild` = layers whose imagery must
    // be (re)built; the actual collection mutation happens in one guarded
    // commit after all async prep settles.
    const myGen = ++genRef.current;
    const keptOrBuilt = new Set<string>();
    const pendingBuild: Array<{
      name: string;
      sig: string;
      imageryLayer: Cesium.ImageryLayer;
      old: Cesium.ImageryLayer | null;
    }> = [];

    // Prune cache entries for layers no longer in the list.
    const currentLayerNames = new Set((layers ?? []).map((l: any) => l.uniqueLayerName));
    for (const key of featureRequestsRef.current.keys()) {
      if (!currentLayerNames.has(key)) featureRequestsRef.current.delete(key);
    }
    // A dataset reupload changes the contents behind the same layer name. Drop
    // the client-side feature promise when the ingest lifecycle or backing table
    // changes, otherwise labels/search can continue using the old geometry even
    // after the WMS layer has refreshed.
    for (const layer of layers ?? []) {
      const name = layer?.uniqueLayerName;
      if (!name) continue;
      const version = JSON.stringify([layer.publishStatus, layer.nativeTable, layer.native_table, layer.updatedAt]);
      const previousVersion = featureLayerVersionRef.current.get(name);
      if (previousVersion && previousVersion !== version) {
        featureRequestsRef.current.delete(name);
      }
      featureLayerVersionRef.current.set(name, version);
    }

    const fetchLayerFeatures = (layerName: string) => {
      const cache = featureRequestsRef.current;
      let req = cache.get(layerName);
      if (!req) {
        req = (async () => {
          await acquireSlot();
          try {
            const res = await fetchFeaturesFromLayerAsync({
              layerName,
              maxFeatures: MAX_CLIENT_FEATURES,
              signal: abortRef.current?.signal,
            });
            const count = res?.features?.length ?? 0;
            if (count >= MAX_CLIENT_FEATURES && !warnedLayers.has(layerName)) {
              warnedLayers.add(layerName);
              console.warn(
                `[map] Layer "${layerName}": client-side features capped at ${MAX_CLIENT_FEATURES} ` +
                  `(labels/clustering/search may be partial; full geometry still renders via WMS tiles).`,
              );
            }
            return res;
          } catch (err) {
            // Drop from cache so the next run can retry (a cancelled wave will
            // simply be re-requested when layers settle).
            cache.delete(layerName);
            throw err;
          } finally {
            releaseSlot();
          }
        })();
        cache.set(layerName, req);
      }
      return req;
    };

    // const removeDuplicateWord = (input: string) =>
    //   input?.replace(/\s*Duplicate$/, "");

    const loadWMSLayer = async () => {
      // US-101359 AC-01: sort_order is a z-index. `layers` arrives in
      // top-of-legend-first order (highest sort_order first). Cesium's
      // imageryLayers.add() appends to the *top* of the collection, so we
      // iterate in reverse: the lowest-z layer is added first, the highest-z
      // layer is added last and ends up on top of the map — matching the
      // legend visually.
      // Only "ready" layers have a usable GeoServer layer. Drop anything still
      // "processing" (and any non-ready) up front so nothing below fetches
      // features, renders WMS, or calls GeoServer for them — and so the
      // "last layer" searchable-features logic keys off the last READY layer.
      // null/undefined publishStatus = legacy rows, treated as ready. (Backend
      // already excludes "failed".)
      const orderedLayers = [...(layers ?? [])].filter((l: any) => !l.publishStatus || l.publishStatus === 'ready').reverse();
      // Each iteration builds a WMS imagery layer that must be stacked in
      // orderedLayers order (lowest-z first). The per-layer work below is async
      // and finishes at different times, so a plain `forEach(async …)` raced:
      // `imageryLayers.add()` ran in completion order, not legend order, and
      // the map z-order no longer matched the legend. Instead, run the prep in
      // parallel but stash each layer's imagery at its fixed index, then add
      // them in order once everything settles (see the ordered add below).
      const imageryByIndex: (Cesium.ImageryLayer | null)[] = [];
      const renderTasks = orderedLayers.map(async (layer: any, layerIdx) => {
        // Only fetch features for layers actually shown on the map. A layer
        // that's toggled off (displayOnMap=false) needs no client features —
        // its geometry isn't rendered, labelled, clustered, or searched — so we
        // skip the (heavy) get-features call entirely. The search index is
        // assembled from the cache in legend order after all tasks settle.
        // if (!['WMS', 'raster'].includes(layer.layerType) && layer.displayOnMap) {
        //   await fetchLayerFeatures(layer.uniqueLayerName);
        // }
        // US-101373 Scenario 5: hidden layers (displayInLegend=false) can still
        // be rendered on the map when the user ticks their visibility checkbox
        // from the "Show Hidden Layers" view. Map rendering is therefore
        // driven by displayOnMap alone; displayInLegend only controls whether
        // a layer is part of the main legend list.
        if (layer.displayOnMap) {
          if (layer.displayLabels) {
            const labelCollection = new Cesium.LabelCollection();
            viewer.scene.primitives.add(labelCollection);
            const features = await fetchLayerFeatures(layer.uniqueLayerName);
            let filteredFeatures = features?.features || [];
            const activeFilter = layer.filter?.find((f: any) => f.name === layer.selectedFilter);
            if (layer.useFilters && activeFilter && activeFilter.expression?.length) {
              filteredFeatures = features.features.filter((feature: any) => {
                let result = true;
                activeFilter.expression.forEach((exp: any, index: number) => {
                  const featureVal = feature?.properties?.[exp.category];
                  let conditionResult = true;
                  switch (exp.condition) {
                    case 'IS':
                      conditionResult = featureVal == exp.value;
                      break;
                    case 'IS NOT':
                      conditionResult = featureVal != exp.value;
                      break;
                    default:
                      conditionResult = true;
                  }
                  const logicalOperator = exp.logicalOperator?.toUpperCase()?.trim();
                  if (index === 0) result = conditionResult;
                  else if (logicalOperator === 'AND') result = result && conditionResult;
                  else if (logicalOperator === 'OR') result = result || conditionResult;
                  else result = result && conditionResult;
                });
                return result;
              });
            }
            filteredFeatures.forEach((feature: any) => {
              const geom = feature.geometry;
              const position = getLabelPosition(geom, layer.labelPosition);
              const labelText = feature.properties?.[layer.labelField] ?? layer.uniqueLayerName;
              const fontSize = Number(layer.fontSize) || 12;
              const xOffset = Number(layer.xAxisOffset) || 0;
              const yOffset = Number(layer.yAxisOffset) || 0;
              const hRadius = Number(layer.haloRadius) || 0;
              const hStrength = Math.min(Math.max(Number(layer.haloStrength) / 100, 0), 1) || 0;
              labelCollection.add({
                position,
                text: String(labelText),
                font: `${fontSize}px ${layer.fontFamily || 'sans-serif'}`,
                fillColor: Cesium.Color.fromCssColorString(layer.labelColor || '#FFFFFF'),
                style: layer.halo ? Cesium.LabelStyle.FILL_AND_OUTLINE : Cesium.LabelStyle.FILL,
                outlineWidth: layer.halo ? Math.max(hRadius, 0) : 0,
                outlineColor: layer.halo
                  ? Cesium.Color.fromCssColorString(layer.haloColor || '#000000').withAlpha(hStrength)
                  : Cesium.Color.TRANSPARENT,
                horizontalOrigin: layer.labelPosition === 'offset_horizontal' ? Cesium.HorizontalOrigin.LEFT : Cesium.HorizontalOrigin.CENTER,
                verticalOrigin: Cesium.VerticalOrigin.CENTER,
                pixelOffset: new Cesium.Cartesian2(xOffset, -yOffset), // Cesium Y is inverted
                disableDepthTestDistance: Number.POSITIVE_INFINITY, // Keeps labels visible through terrain
              } as any);
            });
          }
          if (layer.glowColor || layer.greyScale) {
            removeExistingGlowEntities(viewer, layer.uniqueLayerName);
            if (layer.glow || layer.greyScale) {
              const featuresRes = await fetchLayerFeatures(layer.uniqueLayerName);
              const layerFeatures = featuresRes?.features || [];
              const glowPower = Math.max(0.1, Math.min(1, (Number(layer.glowStrength) || 5) / 10));
              const widthPx = Math.max(1, Number(layer.glowRadius) || 10);
              layerFeatures.forEach((feature: any, idx: number) => {
                const gray = Cesium.Color.GRAY.withAlpha(1.0);
                if (layer.greyScale) {
                  if (feature.geometry.type.includes('Line')) renderGreyscaleLine(viewer, feature, gray, layer.uniqueLayerName, idx);
                  else renderGreyscalePolygon(viewer, feature, gray, layer.uniqueLayerName, idx);
                }
                if (layer.glow) renderGlowOutline(viewer, feature, layer.glowColor || '#00FFFF', glowPower, widthPx, layer.uniqueLayerName, idx);
              });
            }
          }
          const isPointLayer = layer.layerType?.toLowerCase().includes('point');
          if (layer.clustering && isPointLayer) {
            const clusterLayer = new Cesium.CustomDataSource(layer.uniqueLayerName);
            viewer.dataSources.add(clusterLayer);

            // Built-in shapes (shape:*) have no S3 URL — they're drawn as
            // SLD <Mark>s server-side, and Cesium's cluster path falls back
            // to its `point` primitive when iconUrl is empty.
            const baseIconUrl = layer.selectedIcon && !isBuiltInShape(layer.selectedIcon) ? await loadUrl(layer.selectedIcon) : '';
            const uniqueIconUrl = layer.uniqueIcon && !isBuiltInShape(layer.uniqueIcon) ? await loadUrl(layer.uniqueIcon) : '';

            const features = await fetchLayerFeatures(layer.uniqueLayerName);
            features?.features?.forEach((feature: any) => {
              const geom = feature.geometry;

              const addPointEntity = (coord: number[]) => {
                if (!coord || coord.length < 2) return;

                let activeFill = layer.fill || '#fff';
                let activeIcon = baseIconUrl;

                if (layer.symbology === 'unique' && layer.symbologyUniqueKey) {
                  if (String(feature.properties[layer.symbologyUniqueKey]) === String(layer.symbologyUniqueValue)) {
                    activeFill = layer.uniqueFill || activeFill;
                    activeIcon = uniqueIconUrl || activeIcon;
                  }
                }

                clusterLayer.entities.add({
                  position: Cesium.Cartesian3.fromDegrees(coord[0], coord[1]),
                  billboard: activeIcon
                    ? {
                        image: activeIcon,
                        width: Number(layer.iconSize) || 24,
                        height: Number(layer.iconSize) || 24,
                        rotation: Cesium.Math.toRadians(Number(layer.iconRotation) || 0),
                        heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
                      }
                    : undefined,
                  point: !activeIcon
                    ? {
                        pixelSize: 10,
                        color: Cesium.Color.fromCssColorString(activeFill),
                        outlineColor: Cesium.Color.BLACK,
                        outlineWidth: 1,
                        heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
                      }
                    : undefined,
                  // properties: feature.properties,
                  properties: new Cesium.PropertyBag({
                    ...feature.properties,
                    _spiderStyle: { activeIcon, activeFill, size: layer.iconSize },
                  }),
                });
              };

              if (geom.type === 'Point') {
                addPointEntity(geom.coordinates);
              } else if (geom.type === 'MultiPoint') {
                geom.coordinates.forEach((c: any) => addPointEntity(c));
              }
            });

            clusterLayer.clustering.enabled = true;
            clusterLayer.clustering.pixelRange = Math.max(1, Number(layer.clusterRadius) || 40);
            clusterLayer.clustering.minimumClusterSize = 2;
            clusterLayer.clustering.clusterEvent.addEventListener((clusteredEntities: any, cluster: any) => {
              cluster.label.show = true;
              cluster.label.text = `${clusteredEntities.length}`;
              cluster.label.font = 'bold 18px sans-serif';
              cluster.label.fillColor = Cesium.Color.BLACK;
              cluster.label.outlineColor = Cesium.Color.BLACK;
              cluster.label.outlineWidth = 2;
              cluster.label.verticalOrigin = Cesium.VerticalOrigin.CENTER;
              cluster.label.horizontalOrigin = Cesium.HorizontalOrigin.CENTER;
              const canvas = document.createElement('canvas');
              const size = 128;
              canvas.width = size;
              canvas.height = size;
              const ctx = canvas.getContext('2d')!;
              const centerX = size / 2;
              const centerY = size / 2;
              const radius = 32;
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
              ctx.fillStyle = layer.clusterColor || '#F3D34B';
              ctx.fill();
              ctx.strokeStyle = 'white';
              ctx.lineWidth = 6;
              ctx.stroke();

              const count = clusteredEntities.length;
              ctx.fillStyle = 'black';
              const fontSize = count > 99 ? 16 : 18;
              ctx.font = `bold ${fontSize}px sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';

              ctx.fillText(String(count), centerX, centerY);

              cluster.billboard.show = true;
              cluster.billboard.image = canvas.toDataURL();

              cluster.billboard.eyeOffset = new Cesium.Cartesian3(0, 0, 0);

              cluster.billboard.id = {
                isCluster: true,
                styles: clusteredEntities.map((e: any) => e.properties._spiderStyle.getValue()),
              };

              cluster.label.show = false;
            });

            if (layer.spiralOnHover) {
              handler.setInputAction((movement: any) => {
                const picked = viewer.scene.pick(movement.endPosition);
                spiderfyEntities.forEach((e) => viewer.entities.remove(e));
                spiderfyEntities = [];

                if (Cesium.defined(picked) && picked.id?.isCluster) {
                  const centerPos = picked.primitive.position;
                  const styles = picked.id.styles;
                  const count = styles.length;

                  const cameraHeight = viewer.camera.positionCartographic.height;
                  const baseRadiusInDegrees = cameraHeight / 1500000;

                  styles.forEach((style: any, i: number) => {
                    const angle = (i / count) * Math.PI * 2;
                    const spiralFactor = baseRadiusInDegrees + Math.floor(i / 8) * (baseRadiusInDegrees * 0.5);

                    const carto = Cesium.Cartographic.fromCartesian(centerPos);
                    const pos = Cesium.Cartesian3.fromDegrees(
                      Cesium.Math.toDegrees(carto.longitude) + Math.cos(angle) * spiralFactor,
                      Cesium.Math.toDegrees(carto.latitude) + Math.sin(angle) * spiralFactor,
                    );

                    spiderfyEntities.push(
                      viewer.entities.add({
                        polyline: {
                          positions: [centerPos, pos],
                          width: 2,
                          material: Cesium.Color.WHITE.withAlpha(0.6),
                          zIndex: 9999,
                        },
                      }),
                    );

                    spiderfyEntities.push(
                      viewer.entities.add({
                        position: pos,
                        billboard: style?.activeIcon
                          ? {
                              image: style.activeIcon,
                              width: style.size || 24,
                              height: style.size || 24,
                              disableDepthTestDistance: Number.POSITIVE_INFINITY,
                              eyeOffset: new Cesium.Cartesian3(0, 0, -5000),
                            }
                          : undefined,
                        point: !style?.activeIcon
                          ? {
                              pixelSize: 12,
                              color: Cesium.Color.fromCssColorString(style?.activeFill || '#AAAAAA'),
                              outlineColor: Cesium.Color.BLACK,
                              outlineWidth: 2,
                              disableDepthTestDistance: Number.POSITIVE_INFINITY,
                              // eyeOffset: new Cesium.Cartesian3(0, 0, -5000)
                            }
                          : undefined,
                      }),
                    );
                  });
                }
              }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
            }

            const zoomToHeight: Record<number, number> = {
              4: 20000000,
              6: 10000000,
              8: 5000000,
              10: 1200000,
              12: 400000,
              14: 100000,
              16: 20000,
              18: 8000,
            };
            const disableHeight = zoomToHeight[Number(layer.disableAt)] ?? 400000;
            const manageClustering = () => {
              const cameraHeight = viewer.camera.positionCartographic.height;
              const shouldCluster = layer.clustering && cameraHeight >= disableHeight;
              clusterLayer.clustering.enabled = shouldCluster;
              clusterLayer.show = shouldCluster;
            };
            track(viewer.camera.changed.addEventListener(manageClustering));
            manageClustering();
          } else {
            const wmsSig = wmsSignature(layer);
            // A backend-created row (duplicate layer) leaves `symbology` and the
            // flat columns null; resolveSingleConfig picks what it renders with.
            const symbology = layer.symbology || 'single';
            const singleCfg = resolveSingleConfig(layer);
            const existingImagery = imageryRegistryRef.current.get(layer.uniqueLayerName);
            const reusable = !!existingImagery && existingImagery.sig === wmsSig && imLayers.contains(existingImagery.imageryLayer);
            // Reuse unchanged layers' tiles (and skip the SLD PUT) so a single
            // edit doesn't re-render and re-request every layer's imagery.
            let imageryLayer: Cesium.ImageryLayer | null = reusable ? existingImagery!.imageryLayer : null;
            if (!reusable && !['WMS', 'raster'].includes(layer.layerType)) {
              const singleIcon = singleCfg ? singleCfg.icon?.key : layer.selectedIcon;
              // Skip loadUrl for built-in shapes — the SLD generator emits
              // a <Mark><WellKnownName> for those, no icon URL needed.
              const iconUrl = singleIcon && !isBuiltInShape(singleIcon) ? await loadUrl(singleIcon) : '';
              const uniqueIconUrl = layer.uniqueIcon && !isBuiltInShape(layer.uniqueIcon) ? await loadUrl(layer.uniqueIcon) : '';
              const quantityIconUrl = layer.quantityIcon && !isBuiltInShape(layer.quantityIcon) ? await loadUrl(layer.quantityIcon) : '';

              if (symbology === 'heatmap' && layer.symbologyConfig?.type === 'heatmap') {
                // US-101677: heatmap driven by the typed config (no weight
                // column required — unweighted when weightAttribute is null).
                const cfg = layer.symbologyConfig;
                await applyHeatmapStyleAsync({
                  layerName: layer.uniqueLayerName,
                  config: {
                    layerType: layer.layerType,
                    radius: cfg.groupRadius,
                    weightAttribute: cfg.weightAttribute || undefined,
                    colorRamp: cfg.colorRamp,
                    opacity: cfg.opacity,
                    minZoom: cfg.minZoom,
                    maxZoom: cfg.maxZoom,
                  },
                });
              } else if (symbology === 'heatmap' && layer.selectedColumn) {
                // Legacy fallback for layers saved before symbology_config.
                const heatmapConfig = {
                  layerType: layer.layerType,
                  radius: layer.groupRadius,
                  weightAttribute: layer.selectedColumn,
                  colorRamp: layer.selectedColourRamp,
                  opacity: layer.opacity,
                  minZoom: layer.minZoom,
                  maxZoom: layer.maxZoom,
                };
                await applyHeatmapStyleAsync({
                  layerName: layer.uniqueLayerName,
                  config: heatmapConfig,
                });
              } else if (symbology === 'quantity' && layer.symbologyConfig?.type === 'quantity') {
                // US-101669/70: render one rule per class break, driven by the
                // typed config. Resolve presigned URLs for image icons.
                const cfg = layer.symbologyConfig;
                const classes = await Promise.all(
                  (cfg.classes ?? []).map(async (c: any) => {
                    const iconKey = c.symbology?.icon?.key;
                    const needsUrl = iconKey && !isBuiltInShape(iconKey);
                    const resolvedUrl = needsUrl ? await loadUrl(iconKey) : false;
                    return {
                      min: c.min,
                      max: c.max,
                      minOp: c.minOp,
                      maxOp: c.maxOp,
                      symbology: c.symbology,
                      iconUrl: resolvedUrl || false,
                    };
                  }),
                );
                await applyQuantityClassStyleAsync({
                  layerName: layer.uniqueLayerName,
                  layerType: layer.layerType,
                  field: cfg.field,
                  classes,
                });
              } else if (symbology === 'quantity' && layer.quantityField) {
                await applyQuantityStyleAsync({
                  layerName: layer.uniqueLayerName,
                  layerType: layer.layerType,
                  field: layer.quantityField,
                  mode: layer.classBreaksMode,
                  numberOfBreaks: layer.numberOfBreaks || 5,
                  colorRamp: layer.colorRamp,
                  iconSettings:
                    layer.layerType === 'Point' || layer.layerType === 'MultiPoint'
                      ? {
                          icon: layer.quantityIcon,
                          iconSize: layer.quantityIconSize ?? 12,
                          iconRotation: layer.quantityIconRotation ?? 0,
                          iconType: layer.quantityIconType,
                          iconUrl: quantityIconUrl || false,
                        }
                      : undefined,
                  fillOpacity: layer.quantityFillOpacity / 100,
                  outlineSettings: {
                    outlineColor: layer.quantityOutline,
                    outlineOpacity: layer.quantityOutlineOpacity / 100,
                    outlineWeight: layer.quantityOutlineWeight ?? 1,
                  },
                });
              } else if (symbology === 'unique' && layer.symbologyConfig?.type === 'unique') {
                // US-101663/4: render one rule per category. Resolve presigned
                // URLs for any image icons (built-in shapes need none — they
                // become <Mark>s server-side). With zero categories (all
                // removed) the generator emits a matches-nothing rule, so the
                // layer renders nothing (Scenario 6).
                const cfg = layer.symbologyConfig;
                const categories = await Promise.all(
                  (cfg.categories ?? []).map(async (cat: any) => {
                    const iconKey = cat.symbology?.icon?.key;
                    const needsUrl = iconKey && !isBuiltInShape(iconKey);
                    const resolvedUrl = needsUrl ? await loadUrl(iconKey) : false;
                    return { value: cat.value, symbology: cat.symbology, iconUrl: resolvedUrl || false };
                  }),
                );
                await applyUniqueStyleAsync({
                  layerName: layer.uniqueLayerName,
                  layerType: layer.layerType,
                  field: cfg.field,
                  categories,
                });
              } else {
                // singleCfg wins over the flat columns; the fallbacks keep a row
                // with neither from producing an invalid SLD (empty colour, NaN
                // opacity, or the literal string "undefined" in <Size>/stroke-width)
                // that GeoServer rejects, leaving the layer unrendered.
                //
                // iconSize must use ?? (not a truthy ternary on singleCfg): a saved
                // single config with icon:null still makes singleCfg truthy, and
                // singleCfg.icon?.size is undefined — which GeoServer rejects as
                // Invalid measure 'undefined'.
                await applyDynamicStyleAsync({
                  layerName: layer.uniqueLayerName,
                  layerType: layer.layerType,
                  symbology,
                  fillColor: singleCfg?.fill || layer.fill || '#AAAAAA',
                  strokeColor: singleCfg?.outline || layer.outline || '#000000',
                  fillOpacity: (singleCfg?.fillOpacity ?? layer.fillOpacity ?? 100) / 100,
                  fillType: singleCfg?.fillType ?? layer.fillType,
                  strokeWidth: singleCfg?.outlineWeight ?? layer.outlineWeight ?? 1,
                  strokeOpacity: (singleCfg?.outlineOpacity ?? layer.outlineOpacity ?? 100) / 100,
                  outlineType: singleCfg?.outlineType ?? layer.outlineType,
                  key: layer.symbologyUniqueKey,
                  value: layer.symbologyUniqueValue,
                  uniqueFill: layer.uniqueFill,
                  uniqueFillOpacity: layer.uniqueFillOpacity / 100,
                  uniqueOutline: layer.uniqueOutline,
                  uniqueOutlineType: layer.uniqueOutlineType,
                  uniqueOutlineOpacity: layer.uniqueOutlineOpacity / 100,
                  uniqueOutlineWeight: layer.uniqueOutlineWeight ?? 1,
                  selectedIcon: singleIcon,
                  iconSize: singleCfg?.icon?.size ?? layer.iconSize ?? 12,
                  iconRotation: singleCfg?.icon?.rotation ?? layer.iconRotation ?? 0,
                  iconType: singleCfg?.icon?.type ?? layer.iconType,
                  iconUrl,
                  uniqueIcon: layer.uniqueIcon,
                  uniqueIconSize: layer.uniqueIconSize ?? 12,
                  uniqueIconRotation: layer.uniqueIconRotation ?? 0,
                  uniqueIconType: layer.uniqueIconType,
                  uniqueIconUrl,
                });
              }
            }
            // const oldImageryProvider = new Cesium.WebMapServiceImageryProvider({
            //   url: `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/wms`,
            //   layers:
            //     layer?.layerType === "WMS"
            //       ? layer?.layerTitle
            //       : removeDuplicateWord(layer.uniqueLayerName),
            //   parameters: {
            //     transparent: true,
            //     format: "image/png",
            //     styles:
            //       layer.symbology === "heatmap"
            //         ? `heatmap_style_${layer.uniqueLayerName}`
            //         : layer.symbology === "quantity"
            //           ? `quantity_style_${layer.uniqueLayerName}`
            //           : layer.layerType === "MultiPolygon"
            //             ? `dynamic_style_${layer.uniqueLayerName}`
            //             : "",
            //   },
            // });

            // US-101677: GeoServer's vec:Heatmap normalises its output to the
            // max density *within each request*, so tiling the layer makes
            // every WMS tile use a different colour scaling → hard seams (a
            // colour discontinuity, not a geometry gap, so a render buffer
            // alone can't fix it). Render the heatmap as ONE image over the
            // data extent: a single request → a single normalisation → a
            // continuous, seam-free heatmap.
            const isHeatmap = symbology === 'heatmap';

            if (!reusable && isHeatmap) {
              try {
                // Derive the data extent. Prefer the layer's authoritative
                // WmsBound (same source the WMS rectangle uses below) so we
                // DON'T download the whole layer just to compute a bbox; only
                // fall back to scanning (capped) features when bounds are absent.
                let minLon = Infinity;
                let minLat = Infinity;
                let maxLon = -Infinity;
                let maxLat = -Infinity;
                const hasLayerBounds =
                  layer.minX != null &&
                  layer.minY != null &&
                  layer.maxX != null &&
                  layer.maxY != null &&
                  Number.isFinite(parseFloat(layer.minX)) &&
                  parseFloat(layer.maxX) > parseFloat(layer.minX) &&
                  parseFloat(layer.maxY) > parseFloat(layer.minY);
                if (hasLayerBounds) {
                  minLon = parseFloat(layer.minX);
                  minLat = parseFloat(layer.minY);
                  maxLon = parseFloat(layer.maxX);
                  maxLat = parseFloat(layer.maxY);
                } else {
                  const featData = await fetchLayerFeatures(layer.uniqueLayerName);
                  for (const f of featData?.features ?? []) {
                    const g = f?.geometry;
                    if (!g) continue;
                    const coordList = g.type === 'Point' ? [g.coordinates] : g.type === 'MultiPoint' ? g.coordinates : [];
                    for (const c of coordList) {
                      if (!Array.isArray(c) || c.length < 2) continue;
                      const [lon, lat] = c;
                      if (lon < minLon) minLon = lon;
                      if (lat < minLat) minLat = lat;
                      if (lon > maxLon) maxLon = lon;
                      if (lat > maxLat) maxLat = lat;
                    }
                  }
                }
                if (Number.isFinite(minLon) && maxLon > minLon && maxLat > minLat) {
                  // Pad so edge kernels aren't clipped at the image border.
                  const padX = Math.max((maxLon - minLon) * 0.15, 0.1);
                  const padY = Math.max((maxLat - minLat) * 0.15, 0.1);
                  const bMinX = minLon - padX;
                  const bMinY = minLat - padY;
                  const bMaxX = maxLon + padX;
                  const bMaxY = maxLat + padY;
                  const aspect = Math.abs((bMaxX - bMinX) / (bMaxY - bMinY)) || 1;
                  const MAX_DIM = 2048;
                  const width = aspect >= 1 ? MAX_DIM : Math.max(1, Math.round(MAX_DIM * aspect));
                  const height = aspect >= 1 ? Math.max(1, Math.round(MAX_DIM / aspect)) : MAX_DIM;
                  // The configured radius is only meaningful once converted against
                  // this image; `env` overrides the default baked into the SLD.
                  const heatmapCfg = layer.symbologyConfig?.type === 'heatmap' ? layer.symbologyConfig : null;
                  const radiusPixels = radiusToPixels(
                    Number(heatmapCfg ? heatmapCfg.groupRadius : layer.groupRadius),
                    heatmapCfg ? heatmapCfg.radiusUnit : 'pixels',
                    { minLon: bMinX, minLat: bMinY, maxLon: bMaxX, maxLat: bMaxY },
                    width,
                  );
                  const buffer = Math.min(Math.max(radiusPixels * 3, 100), 512);
                  const heatmapUrl =
                    `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/mapaki/wms?service=WMS&version=1.1.1&request=GetMap` +
                    `&layers=mapaki:${layer.uniqueLayerName}&bbox=${bMinX},${bMinY},${bMaxX},${bMaxY}` +
                    `&width=${width}&height=${height}&srs=EPSG:4326&format=image/png&transparent=true&buffer=${buffer}` +
                    `&env=radius:${radiusPixels}`;
                  const heatmapProvider = await Cesium.SingleTileImageryProvider.fromUrl(heatmapUrl, {
                    rectangle: Cesium.Rectangle.fromDegrees(bMinX, bMinY, bMaxX, bMaxY),
                  });
                  imageryLayer = new Cesium.ImageryLayer(heatmapProvider);
                  // Added below in legend order (see imageryByIndex).
                }
              } catch (e) {
                console.error('Heatmap single-tile render failed', e);
              }
            }

            if (!imageryLayer) {
              const imageryProvider = new Cesium.WebMapServiceImageryProvider({
                url: `${ApiRoutes.GEOSERVER_BASE_URL}${WMS_PATH}`,
                layers: `mapaki:${layer.uniqueLayerName}`,
                parameters: {
                  service: 'WMS',
                  version: '1.1.1',
                  request: 'GetMap',
                  transparent: true,
                  format: 'image/png',
                  tiled: true,
                },
                tilingScheme: new Cesium.GeographicTilingScheme(),
                tileWidth: 256,
                tileHeight: 256,
                rectangle: layer.minX
                  ? Cesium.Rectangle.fromDegrees(parseFloat(layer.minX), parseFloat(layer.minY), parseFloat(layer.maxX), parseFloat(layer.maxY))
                  : undefined,
              });

              imageryLayer = new Cesium.ImageryLayer(imageryProvider);
              // Added below in legend order (see imageryByIndex).
            }
            if (!reusable && imageryLayer) {
              // Defer the collection mutation to the post-await commit so old
              // and new imagery swap atomically (no blank flash, no race).
              pendingBuild.push({
                name: layer.uniqueLayerName,
                sig: wmsSig,
                imageryLayer,
                old: existingImagery?.imageryLayer ?? null,
              });
            }
            if (imageryLayer) {
              // Tag for per-layer popup gating in useCesiumPopup (pick → layer config).
              (imageryLayer as any).mapakiLayerName = layer.uniqueLayerName;
              keptOrBuilt.add(layer.uniqueLayerName);
              const activeImagery = imageryLayer;
              imageryByIndex[layerIdx] = imageryLayer;
              const listener = () => {
                const cameraHeight = viewer.camera.positionCartographic.height;
                const maxZoomOutHeight = layer.scaleRange?.min || 200000000;
                const maxZoomInHeight = layer.scaleRange?.max || 100;
                activeImagery.show = cameraHeight >= maxZoomInHeight && cameraHeight <= maxZoomOutHeight;
              };
              track(viewer.scene.postRender.addEventListener(listener));
            }
          }
        }
      });

      // Wait for every layer's prep (style PUTs, feature/icon fetches, heatmap
      // tiling) to settle, then add the imagery bottom-up (lowest-z first) so
      // the top-of-legend layer — last in orderedLayers — ends up on top of the
      // map, matching the legend exactly regardless of prep timing.
      await Promise.allSettled(renderTasks);

      // Superseded by a newer run (or torn down) while awaiting — leave the
      // imagery collection alone. Newly-built imagery here was never added, so
      // it's just discarded (GC'd).
      if (disposed || genRef.current !== myGen) return;

      // 1. Remove imagery for layers gone or no longer shown this run.
      for (const [name, entry] of imageryRegistryRef.current) {
        if (!keptOrBuilt.has(name)) {
          try {
            if (imLayers.contains(entry.imageryLayer)) imLayers.remove(entry.imageryLayer, true);
          } catch {
            /* already gone */
          }
          imageryRegistryRef.current.delete(name);
        }
      }
      // 2. Swap in (re)built imagery: drop the previous tile layer, add the new.
      for (const built of pendingBuild) {
        if (built.old) {
          try {
            if (imLayers.contains(built.old)) imLayers.remove(built.old, true);
          } catch {
            /* already gone */
          }
        }
        try {
          if (!imLayers.contains(built.imageryLayer)) imLayers.add(built.imageryLayer);
        } catch {
          /* viewer torn down */
        }
        imageryRegistryRef.current.set(built.name, { sig: built.sig, imageryLayer: built.imageryLayer });
      }
      // 3. Re-stack to legend order WITHOUT re-fetching tiles. orderedLayers is
      // lowest-z first, so raising each to the top in turn leaves the highest-z
      // layer on top; the base imagery stays at index 0.
      imageryByIndex.forEach((imageryLayer) => {
        if (imageryLayer) {
          try {
            imLayers.raiseToTop(imageryLayer);
          } catch {
            /* not in collection */
          }
        }
      });

      // Build the map search index from ONLY the layers shown on the map, in
      // legend order (top-of-legend first — `layers` arrives in that order).
      // Pulls from the warmed feature cache, so it adds no extra requests.
      const searchable: any[] = [];
      for (const layer of layers ?? []) {
        if (layer.publishStatus && layer.publishStatus !== 'ready') continue;
        if (['WMS', 'raster'].includes(layer.layerType) || !layer.displayOnMap) continue;
        const req = featureRequestsRef.current.get(layer.uniqueLayerName);
        if (!req) continue;
        try {
          const res = await req;
          for (const f of res?.features ?? []) searchable.push({ ...f, layerName: layer.uniqueLayerName });
        } catch {
          /* fetch failed/aborted — skip this layer */
        }
      }
      if (genRef.current === myGen && !disposed) setSearchableFeatures(searchable);
    };

    loadWMSLayer();

    // Cleanup: remove every listener/handler this run registered so they don't
    // pile up across re-runs (the leak). Guarded with try/catch because on
    // unmount the viewer may already be destroyed.
    return () => {
      disposed = true;
      for (const remove of removers) {
        try {
          remove();
        } catch {
          /* listener already gone */
        }
      }
      removers.length = 0;
      try {
        if (!handler.isDestroyed()) handler.destroy();
      } catch {
        /* canvas/viewer already torn down */
      }
      for (const e of spiderfyEntities) {
        try {
          viewer.entities.remove(e);
        } catch {
          /* viewer already destroyed */
        }
      }
      spiderfyEntities = [];
    };
  }, [layers, isViewerReady]);
};
